import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import PasswordTextField from "../components/form/fields/PasswordTextField";
import PasswordCheckTextField from "../components/form/fields/PasswordCheckTextField";
import ErrorAlert from "../components/alerts/ErrorAlert";
import { activateUser } from "../app/api/axios/services/public.service";
import {
  getPasswordPolicyChecks,
  isPasswordPolicyValid,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_POLICY_RULES,
} from "../utils/passwordPolicy";

// ----------------------------------------------------------------------

const STRENGTH_LEVELS = [
  { minScore: 0, label: "", color: "error" },
  { minScore: 1, label: "Sehr schwach", color: "error" },
  { minScore: 2, label: "Schwach", color: "warning" },
  { minScore: 3, label: "Mittel", color: "warning" },
  { minScore: 4, label: "Gut", color: "success" },
  { minScore: 5, label: "Stark", color: "success" },
];

function getStrength(password) {
  if (!password) return { score: 0, label: "", color: "error", checks: {} };
  const checks = getPasswordPolicyChecks(password);
  const score = Object.values(checks).filter(Boolean).length;
  const level = [...STRENGTH_LEVELS].reverse().find((l) => score >= l.minScore);
  return { score, checks, ...level };
}

// ----------------------------------------------------------------------

function MissingTokenState() {
  const theme = useTheme();
  return (
    <CenteredLayout>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            maxWidth: 460,
            width: "100%",
            borderRadius: 3,
            textAlign: "center",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <IconCircle icon="eva:alert-circle-fill" color={theme.palette.error.main} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Link ungültig
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
            Der Aktivierungslink ist ungültig oder bereits abgelaufen. Bitte
            wende dich an deine Kontaktperson, um einen neuen Link zu erhalten.
          </Typography>
          <Button
            variant="outlined"
            href="/login"
            startIcon={<Icon icon="eva:arrow-back-fill" />}
          >
            Zur Anmeldung
          </Button>
        </Paper>
      </motion.div>
    </CenteredLayout>
  );
}

function SuccessState() {
  const theme = useTheme();
  return (
    <CenteredLayout>
      <motion.div
        initial={{ opacity: 0, scale: 0.88 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            maxWidth: 460,
            width: "100%",
            borderRadius: 3,
            textAlign: "center",
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
          }}
        >
          <IconCircle
            icon="eva:checkmark-circle-2-fill"
            color={theme.palette.success.main}
            size={80}
            iconSize={48}
          />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Account aktiviert!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Dein Passwort wurde erfolgreich festgelegt.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Du wirst gleich zur Anmeldung weitergeleitet&hellip;
          </Typography>
          <CircularProgress size={22} color="success" />
        </Paper>
      </motion.div>
    </CenteredLayout>
  );
}

// ----------------------------------------------------------------------

export default function ActivateAccountPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const { handleSubmit, control, watch } = useForm({
    defaultValues: { password: "", checkPassword: "" },
  });

  const passwordValue = watch("password");
  const strength = useMemo(() => getStrength(passwordValue), [passwordValue]);

  const strengthColor = {
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    success: theme.palette.success.main,
  }[strength.color];

  const handleCheckMatch = (checkPwd) => {
    if (watch("password") !== checkPwd) {
      return "Die Passwörter stimmen nicht überein";
    }
    return true;
  };

  const missingToken = useMemo(() => !token, [token]);

  const onSubmit = async (data) => {
    if (!token) {
      setErrorMessage("Aktivierungslink ist ungültig.");
      return;
    }
    setErrorMessage("");
    setIsSubmitting(true);
    try {
      const response = await activateUser(token, { password: data.password });
      if (response?.data?.status === "SUCCESS") {
        setIsSuccess(true);
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setErrorMessage("Aktivierung fehlgeschlagen. Bitte versuche es erneut.");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Aktivierung fehlgeschlagen. Bitte versuche es erneut.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (missingToken) return <MissingTokenState />;
  if (isSuccess) return <SuccessState />;

  return (
    <CenteredLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: "100%" }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 480, md: 860 },
            mx: "auto",
          }}
        >
          <Grid
            container
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: theme.customShadows.z24,
            }}
          >
            {/* ── Left panel: branding + explainer (desktop only) ── */}
            {!isMobile && (
              <Grid
                item
                md={5}
                sx={{
                  background: `linear-gradient(155deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  p: 5,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <Icon
                  icon="eva:shield-fill"
                  width={52}
                  style={{ marginBottom: 24, opacity: 0.9 }}
                />
                <Typography variant="h4" fontWeight={800} sx={{ mb: 2, lineHeight: 1.25 }}>
                  Willkommen!
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.82, mb: 4, lineHeight: 1.75 }}
                >
                  Du wurdest eingeladen. Lege jetzt dein persönliches Passwort
                  fest – das dauert nur einen Moment.
                </Typography>

                {/* Step indicators */}
                {[
                  { icon: "eva:email-fill", text: "Du hast eine Einladung erhalten" },
                  { icon: "eva:lock-fill", text: "Wähle ein sicheres Passwort" },
                  { icon: "eva:log-in-fill", text: "Melde dich an und leg los!" },
                ].map((step, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        bgcolor: alpha("#fff", 0.15),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon icon={step.icon} width={17} />
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.88, lineHeight: 1.4 }}>
                      {step.text}
                    </Typography>
                  </Box>
                ))}
              </Grid>
            )}

            {/* ── Right panel: form ── */}
            <Grid
              item
              xs={12}
              md={7}
              component={Paper}
              elevation={0}
              square
              sx={{
                p: { xs: 3, sm: 5 },
                borderRadius: { xs: 3, md: 0 },
              }}
            >
              {/* Mobile header */}
              {isMobile && (
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <IconCircle
                    icon="eva:shield-fill"
                    color={theme.palette.primary.main}
                  />
                </Box>
              )}

              <Typography variant="h5" fontWeight={700} gutterBottom>
                Passwort festlegen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Wähle ein sicheres, persönliches Passwort. Du verwendest es
                künftig zusammen mit deiner E-Mail-Adresse zum Anmelden.
              </Typography>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Grid container spacing={2.5}>
                  {/* Password field + strength */}
                  <Grid item xs={12}>
                    <PasswordTextField
                      fullWidth
                      control={control}
                      required
                      label="Neues Passwort"
                      additionalRules={{
                        validate: (value) =>
                          isPasswordPolicyValid(value) || PASSWORD_POLICY_MESSAGE,
                      }}
                    />

                    {/* Strength bar — only when user has started typing */}
                    {passwordValue.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Passwortstärke
                          </Typography>
                          {strength.label && (
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: strengthColor }}
                            >
                              {strength.label}
                            </Typography>
                          )}
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(strength.score / PASSWORD_POLICY_RULES.length) * 100}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.grey[500], 0.16),
                            "& .MuiLinearProgress-bar": {
                              bgcolor: strengthColor,
                              borderRadius: 3,
                              transition: "width 0.4s ease",
                            },
                          }}
                        />
                        {/* Requirements chips */}
                        <Box
                          sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 0.75 }}
                        >
                          {PASSWORD_POLICY_RULES.map(({ key, label }) => {
                            const met = strength.checks?.[key];
                            return (
                              <Chip
                                key={key}
                                size="small"
                                label={label}
                                icon={
                                  <Icon
                                    icon={
                                      met
                                        ? "eva:checkmark-circle-2-fill"
                                        : "eva:radio-button-off-fill"
                                    }
                                    width={13}
                                    style={{ marginLeft: 4 }}
                                  />
                                }
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 24,
                                  bgcolor: met
                                    ? alpha(theme.palette.success.main, 0.1)
                                    : alpha(theme.palette.grey[500], 0.1),
                                  color: met
                                    ? theme.palette.success.dark
                                    : theme.palette.text.secondary,
                                  "& .MuiChip-icon": { color: "inherit" },
                                  transition: "all 0.25s ease",
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  {/* Confirm password */}
                  <Grid item xs={12}>
                    <PasswordCheckTextField
                      handleCheckMatch={handleCheckMatch}
                      name="checkPassword"
                      label="Passwort wiederholen"
                      control={control}
                      fullWidth
                      required
                    />
                  </Grid>

                  {/* Error alert */}
                  {errorMessage && (
                    <Grid item xs={12}>
                      <ErrorAlert errorMessage={errorMessage} />
                    </Grid>
                  )}

                  {/* Submit */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <Icon icon="eva:checkmark-circle-2-fill" />
                        )
                      }
                      sx={{
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 400,
                        borderRadius: 2,
                      }}
                    >
                      {isSubmitting ? "Wird aktiviert…" : "Account jetzt aktivieren"}
                    </Button>
                  </Grid>
                </Grid>
              </form>

              {/* Already activated link */}
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 3 }}
              >
                Bereits aktiviert?{" "}
                <Typography
                  component="a"
                  href="/login"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Zur Anmeldung
                </Typography>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </motion.div>
    </CenteredLayout>
  );
}

// ----------------------------------------------------------------------
// Shared layout helpers

function CenteredLayout({ children }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg,
          ${alpha(theme.palette.primary.lighter, 0.55)} 0%,
          ${alpha(theme.palette.secondary.lighter, 0.35)} 100%)`,
        p: { xs: 2, sm: 3 },
      }}
    >
      {children}
    </Box>
  );
}

function IconCircle({
  icon,
  color,
  size = 72,
  iconSize = 36,
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: alpha(color, 0.12),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 2,
      }}
    >
      <Icon icon={icon} width={iconSize} color={color ?? theme.palette.primary.main} />
    </Box>
  );
}
